import { Component, Vue } from "vue-property-decorator";
import { AxiosResponse } from "axios";
import { ICliente } from "@/entities";
import { Buffer } from "buffer";

declare module "vue/types/vue" {
  interface Vue {
    cleanUpEmptyStrings(obj: any): any;
    sortAscending(a: any, b: any, sortby: string): number;
    sortDescending(a: any, b: any, sortby: string): number;
    isObjectEmpty(object: any): boolean;

    getStartDate(date?: string | null): string | undefined;
    getStartLocalDate(date?: string | null): string | undefined;
    getEndDate(date?: string | null): string | undefined;
    getEndLocalDate(date?: string | null): string | undefined;
    getThreeDate(date: string): string | undefined;
    isInitialObject(object: any, initialObject: any): boolean;
    downloadFile(response: AxiosResponse | undefined): void;
    buildAndDownloadFile(blobObj: Blob | undefined, filename: string): void;
    makeToast(variant: string, message: string): void;
    convertBase64StringToBlob(base64String: string, type: string): Blob;
    getNumTasa(clienteSelected: ICliente, tasa?: number): number | undefined;
    convertBase64ToBlobNative(base64String: string, contentType?: string): Blob;
    downloadFileFromBlob(blob: Blob, filename: string): void;
  }
}

@Component
export default class VueMethods extends Vue {
  public cleanUpEmptyStrings(obj: Record<string, any>): Record<string, any> {
    const map = new Map(Object.entries(obj));
    map.forEach((value, key) => {
      if (value === "") {
        obj[key] = undefined;
      }
    });
    return obj;
  }

  public sortAscending(a: unknown, b: unknown, sortBy: string): number {
    if (((a as any)[sortBy] || "") < ((b as any)[sortBy] || "")) {
      return -1;
    }
    return ((a as any)[sortBy] || "") > ((b as any)[sortBy] || "") ? 1 : 0;
  }

  public sortDescending(a: unknown, b: unknown, sortBy: string): number {
    if (((a as any)[sortBy] || "") < ((b as any)[sortBy] || "")) {
      return 1;
    }
    return ((a as any)[sortBy] || "") > ((b as any)[sortBy] || "") ? -1 : 0;
  }

  public isObjectEmpty(object: unknown): boolean {
    return Object.values(object as any).every((item: any) => {
      return (
        item === null ||
        item === undefined ||
        item === "" ||
        (item.constructor === Array && item.length === 0)
      );
    });
  }

  public getStartDate(date?: string): string | undefined {
    try {
      if (!date) return undefined;
      return new Date(new Date(date).toDateString()).toISOString();
    } catch {
      return undefined;
    }
  }

  public getStartLocalDate(date?: string): string | undefined {
    try {
      if (!date) return undefined;

      const startDate = new Date(date);
      const day = startDate.getUTCDate();
      const month = startDate.getUTCMonth();
      const year = startDate.getUTCFullYear();

      const startLocalDate = new Date(year, month, day);
      startLocalDate.setTime(
        startLocalDate.valueOf() - startLocalDate.getTimezoneOffset() * 60 * 1000
      );

      return startLocalDate.toISOString();
    } catch {
      return undefined;
    }
  }

  public getEndDate(date?: string): string | undefined {
    try {
      if (!date) return undefined;
      const startDate = new Date(this.getStartDate(date)!);
      startDate.setDate(startDate.getDate() + 1);
      startDate.setMilliseconds(startDate.getMilliseconds() - 1);
      return startDate.toISOString();
    } catch {
      return undefined;
    }
  }

  public getEndLocalDate(date?: string): string | undefined {
    try {
      if (!date) return undefined;
      const startDate = new Date(this.getStartLocalDate(date)!);
      startDate.setDate(startDate.getDate() + 1);
      startDate.setMilliseconds(startDate.getMilliseconds() - 1);
      return startDate.toISOString();
    } catch {
      return undefined;
    }
  }

  public getThreeDate(date: string): string | undefined {
    try {
      if (!date) return undefined;
      const startDate = new Date(this.getStartDate(date)!);
      startDate.setDate(startDate.getDate() - 3);
      return startDate.toISOString();
    } catch {
      return undefined;
    }
  }

  public isInitialObject(object: unknown, initialObject: unknown): boolean {
    return JSON.stringify(initialObject) === JSON.stringify(object);
  }

  public downloadFile(response: AxiosResponse | undefined): void {
    const disposition = response?.headers["content-disposition"];
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(disposition) ?? [];
    const filename = matches[1]?.replace(/['"]/g, "") ?? "";
    const blobObj = response?.data;
    this.buildAndDownloadFile(blobObj, filename);
  }

  public buildAndDownloadFile(blobObj: Blob | undefined, filename: string): void {
    if (blobObj) {
      const url = window.URL.createObjectURL(blobObj);
      const aElement = document.createElement("a");
      aElement.href = url;
      aElement.download = filename;
      aElement.click();
      window.URL.revokeObjectURL(url);
    }
  }

  public convertBase64StringToBlob(base64String: string, type: string): Blob {
    const binaryString = Buffer.from(base64String, "base64");
    return new Blob([binaryString], { type });
  }

  public convertBase64ToBlobNative(
    base64String: string,
    contentType = "application/octet-stream"
  ): Blob {
    try {
      // Decodificar Base64 usando API del navegador en lugar de Buffer
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: contentType });
    } catch (error) {
      console.error("Error converting base64 to blob:", error);
      throw new Error("Failed to convert base64 string to blob");
    }
  }

  downloadFileFromBlob(blob: Blob, filename: string): void {
    try {
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${filename}.txt`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
      this.$bvToast.toast("Error al descargar el archivo", {
        title: "Error",
        variant: "danger",
        solid: true,
      });
    }
  }

  public makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  public getNumTasa(clienteSelected: ICliente, tasa?: number): number | undefined {
    if (clienteSelected.pais.hasTasa && clienteSelected.pais.hasTasaEnteros) {
      return Number(tasa) / 100000;
    } else if (clienteSelected.pais.hasTasa) {
      return Number(tasa);
    }
  }
}
